import React from "react";
import Layout from "../Layout";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MdOutlineEmail } from "react-icons/md";
import { Phone, MapPin } from "lucide-react";
import { indexStore } from "../../../store/indexStore";
import FormContactUs from "./FormContactUs";
import BranchModel from "../../../Models/BranchModel";
import Iterator from "../../utils/Iterator";
import Visibility from "../../utils/Visibility";
import { socialMediaTypes } from "../../../enum/socialMediaType";
import { SettingsSocialMedia } from "../../../Models/SettingsModel";

function ContactUs() {
  const { t } = useTranslation();
  const { lang, branches, name, socialMedia } = indexStore();

  return (
    <Layout title={t("pages.contact")}>
      {/* Hero Section */}
      <section className="py-8 md:py-12 lg:py-16 relative overflow-hidden">
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/3 to-primary/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary via-primary/90 to-primary bg-clip-text text-transparent">
                {t("pages.contact")}
              </span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("contact.sendMassageParagraph")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-8 md:py-12 lg:py-16 bg-gradient-to-br from-muted via-muted/50 to-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Left: Form */}
            <motion.div
              initial={{ opacity: 0, x: lang === "ar" ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card rounded-xl md:rounded-2xl border-2 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 p-6 md:p-8"
            >
              <div className="mb-6">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-foreground">
                  {t("contact.sendMessage")}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("contact.sendMassageParagraph")}
                </p>
              </div>
              <FormContactUs />
            </motion.div>

            {/* Right: Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: lang === "ar" ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card rounded-xl md:rounded-2xl border-2 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 p-6 md:p-8 flex flex-col justify-between"
            >
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">
                  {t("contact.ReachOutDirectly")}
                </h3>
                <ul className="space-y-4 md:space-y-5">
                  <motion.li
                    whileHover={{ x: lang === "ar" ? -5 : 5 }}
                    className="group"
                  >
                    <a
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                      href="mailto:info@krieshrestaurant.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="p-2 bg-primary/10 group-hover:bg-primary/20 rounded-lg transition-colors">
                        <MdOutlineEmail className="text-primary text-xl md:text-2xl" />
                      </div>
                      <span className="text-base md:text-lg text-foreground font-medium">
                        info@krieshrestaurant.com
                      </span>
                    </a>
                  </motion.li>
                  <Iterator
                    data={branches}
                    render={(branch: BranchModel) => {
                      const addressKey = name === "name_en" ? "address_en" : "address_ar";
                      const address = branch[addressKey as keyof BranchModel] as string;
                      const branchName = branch[name === "name_en" ? "name_en" : "name_ar" as keyof BranchModel] as string;

                      return (
                        <Visibility key={branch.id} visible={!!address || !!branch.phone}>
                          <div className="space-y-3">
                            {/* Branch Name and Address */}
                            {address && (
                              <motion.li
                                whileHover={{ x: lang === "ar" ? -5 : 5 }}
                                className="group"
                              >
                                <a
                                  href={branch.address_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex hover:underline items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors group"
                                >
                                  <div className="p-2 bg-primary/10 group-hover:bg-primary/20 rounded-lg transition-colors flex-shrink-0">
                                    <MapPin className="text-primary text-xl md:text-2xl" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs text-foreground mb-1">
                                      {branchName}
                                    </p>
                                    <span className="text-sm md:text-base flex-1">
                                      {address}
                                    </span>
                                  </div>
                                </a>
                              </motion.li>
                            )}
                            
                            {/* Phone Number */}
                            {branch.phone && (
                              <motion.li
                                whileHover={{ x: lang === "ar" ? -5 : 5 }}
                                className="group"
                              >
                                <a
                                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                                  href={`tel:${branch.phone}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <div className="p-2 bg-primary/10 group-hover:bg-primary/20 rounded-lg transition-colors">
                                    <Phone className="text-primary text-xl md:text-2xl" />
                                  </div>
                                  <div className="flex-1">
                                    {branchName && (
                                      <p className="text-xs text-muted-foreground mb-1">
                                        {branchName}
                                      </p>
                                    )}
                                    <span dir="ltr" className="text-base md:text-lg text-foreground font-medium">
                                      {branch.phone}
                                    </span>
                                  </div>
                                </a>
                              </motion.li>
                            )}
                          </div>
                        </Visibility>
                      );
                    }}
                  />
                </ul>
              </div>

              {/* Social Media */}
              <div className="mt-8 pt-6 border-t border-border/50">
                <h3 className="text-lg md:text-xl font-semibold mb-4 text-foreground">
                  {t("contact.ConnectWithUs")}
                </h3>
                <div className="flex gap-4">
                  <Iterator
                    data={socialMedia || []}
                    render={(social: SettingsSocialMedia) => {
                      let url = social.link;
                      if (!url.startsWith("https://") && !url.startsWith("http://") && !url.startsWith("mailto:") && !url.startsWith("tel:")) {
                        url = "https://" + url;
                      }

                      const icon = socialMediaTypes[social.type];

                      // تحديد الألوان حسب النوع (WhatsApp = green، الباقي = primary)
                      const isWhatsApp = social.type === 3;
                      const bgColor = isWhatsApp
                        ? "bg-green-500/10 hover:bg-green-500/20"
                        : "bg-primary/10 hover:bg-primary/20";
                      const textColor = isWhatsApp ? "text-green-500" : "text-primary";

                      return (
                        <motion.a
                          key={social.id}
                          whileHover={{ scale: 1.2, y: -3 }}
                          whileTap={{ scale: 0.9 }}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className={`p-3 rounded-full ${bgColor} ${textColor} transition-all`}
                          aria-label={social.title}
                          dir={url.startsWith("tel:") || url.startsWith("mailto:") ? "ltr" : undefined}
                        >
                          <div className="text-xl md:text-2xl">
                            {icon}
                          </div>
                        </motion.a>
                      );
                    }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ContactUs;
